import { ComponentProps, FC, ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { Box, Svg, Typography, Card } from '@altermaind/web/ui';
import { CollapsableCardSkeleton } from './CollapsableCardSkeleton';
import { StyledCollapsableCardContent } from './styles';

// simple global event bus
const listeners = new Set<(id: string) => void>();

type Props = ComponentProps<typeof Card> & {
  title?: string;
  titleProps?: ComponentProps<typeof Typography>;
  children: ReactNode;
  isCollapsedOnDefault?: boolean;
  dataTestIdButton?: string;
  onCustomToggle?: () => void;
  closeOthersCollapseOnOpen?: boolean;
  onTitleHoverStyle?: ComponentProps<typeof Typography>['style'];
};

const CollapsableCard: FC<Props> & {
  Skeleton: typeof CollapsableCardSkeleton;
} = ({
  title = '',
  isCollapsedOnDefault = true,
  children,
  titleProps,
  dataTestIdButton = 'collapsable-card-toggle-button',
  onCustomToggle,
  closeOthersCollapseOnOpen = false,
  onTitleHoverStyle,
  ...props
}) => {
  const id = useId();
  const [isHovered, setIsHovered] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedOnDefault);

  const toggleCollapse = useCallback(() => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);

    if (closeOthersCollapseOnOpen && newState === false) {
      // if I am opening, notify all others
      listeners.forEach(cb => cb(id));
    }

    onCustomToggle?.();
  }, [isCollapsed, closeOthersCollapseOnOpen, id, onCustomToggle]);

  // listen to openings of other cards
  useEffect(() => {
    const handler = (openedId: string) => {
      if (closeOthersCollapseOnOpen && openedId !== id) {
        setIsCollapsed(true);
      }
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, [id, closeOthersCollapseOnOpen]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Card {...props}>
      <Box display='flex' flexDirection='row' justifyContent='space-between'>
        <Box onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <Typography
            variant='h3'
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...titleProps}
            style={{
              ...(titleProps?.style || {}),
              ...(isHovered && onTitleHoverStyle ? onTitleHoverStyle : {}),
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          onClick={toggleCollapse}
          height='s'
          style={{
            cursor: 'pointer',
            transition: 'transform 0.5s ease',
            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(-180deg)',
            transformOrigin: 'center center',
          }}
          data-testid={dataTestIdButton}
        >
          <Svg name='down' size='s' />
        </Box>
      </Box>
      <StyledCollapsableCardContent
        style={{
          overflow: 'hidden',
          transition: 'grid-template-rows 0.5s ease-out',
          gridTemplateRows: isCollapsed ? '0fr' : '1fr',
        }}
        display='grid'
        isCollapsed={isCollapsed}
        isCollapsedOnDefault={isCollapsedOnDefault}
      >
        {children}
      </StyledCollapsableCardContent>
    </Card>
  );
};

CollapsableCard.Skeleton = CollapsableCardSkeleton;

export { CollapsableCard };
